/**
 * Proposition d'enrôlement d'une clé d'accès juste après une connexion.
 *
 * `/sign-in` renvoie à l'accueil dès que la session existe (garde serveur), on ne peut donc pas
 * y afficher la proposition. On pose un drapeau de session avant la navigation ;
 * `PasskeyEnrollOffer` (layouts) le lit sur la page d'arrivée et agit là, sans bloquer :
 *  - `'offer'`   (code, lien) : encart « Connectez-vous plus vite la prochaine fois » ;
 *  - `'upgrade'` (mot de passe) : d'abord une création SILENCIEUSE par le gestionnaire de mots
 *    de passe (mise à niveau automatique, Chrome 128+ / Safari 18+), l'encart en repli.
 * Le refus (« Plus tard ») est mémorisé sur l'appareil pour ne pas redemander. La carte
 * « Sécurisez vos connexions » de Mon Espace a son propre refus (« Ne plus proposer »).
 * Ces mémorisations sont PAR APPAREIL, à dessein : chaque appareil a besoin de sa propre clé.
 */
export type PasskeyOfferMode = 'offer' | 'upgrade';

const OFFER_KEY = 'swf.passkey.offer';
const SKIPPED_KEY = 'swf.passkey.skipped';
const NUDGE_DISMISSED_KEY = 'swf.passkey.nudgeDismissed';

const safe = <T>(f: () => T, fallback: T): T => {
  try {
    return f();
  } catch {
    return fallback;
  }
};

export const requestPasskeyOffer = (mode: PasskeyOfferMode = 'offer') =>
  safe(() => window.sessionStorage.setItem(OFFER_KEY, mode), undefined);

export const consumePasskeyOffer = (): PasskeyOfferMode | null =>
  safe(() => {
    const v = window.sessionStorage.getItem(OFFER_KEY);
    window.sessionStorage.removeItem(OFFER_KEY);
    return v === 'offer' || v === 'upgrade' ? v : null;
  }, null);

export const isPasskeyOfferSkipped = (): boolean =>
  safe(() => window.localStorage.getItem(SKIPPED_KEY) === '1', false);

export const skipPasskeyOffer = () =>
  safe(() => window.localStorage.setItem(SKIPPED_KEY, '1'), undefined);

export const isPasskeyNudgeDismissed = (): boolean =>
  safe(() => window.localStorage.getItem(NUDGE_DISMISSED_KEY) === '1', false);

export const dismissPasskeyNudge = () =>
  safe(() => window.localStorage.setItem(NUDGE_DISMISSED_KEY, '1'), undefined);
